import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * Step-2 filter rail, after the catalogue screen of the guided-entry design:
 * categories with counts, the "only matching products" switch, manufacturers,
 * and one reset when anything is active.
 */

export interface FilterCategory {
  id: string;
  name: string;
  slug: string;
  count: number;
}

export interface FilterManufacturer {
  id: string;
  name: string;
  count: number;
}

interface ProductFilterSidebarProps {
  categories: FilterCategory[];
  activeCategory: string;
  onCategoryChange: (slug: string) => void;
  manufacturers: FilterManufacturer[];
  selectedManufacturerIds: string[];
  onToggleManufacturer: (id: string) => void;
  onlyMatching: boolean;
  onToggleOnlyMatching: () => void;
  /** What "matching" is measured against; null while no design exists. */
  matchHint: string | null;
  hasActiveFilters: boolean;
  onReset: () => void;
}

function SectionTitle({ children }: { children: string }) {
  return (
    <div className="dc-type-label mb-2 text-[var(--mute)]">{children}</div>
  );
}

function CheckRow({
  checked,
  label,
  count,
  onClick,
  disabled = false,
}: {
  checked: boolean;
  label: string;
  count?: number;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-left text-[12.5px]',
        'hover:bg-[var(--paper-2)] disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'text-[var(--ink)]' : 'text-[var(--ink-2)]'
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'grid h-[15px] w-[15px] flex-none place-items-center rounded ring-1 ring-inset',
          checked
            ? 'bg-[var(--cobalt-500)] text-white ring-[var(--cobalt-500)]'
            : 'bg-[var(--paper)] ring-[var(--line)]'
        )}
      >
        {checked && <Check className="h-3 w-3" strokeWidth={3} />}
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined && (
        <span className="dc-type-data text-[var(--mute)]">{count}</span>
      )}
    </button>
  );
}

export function ProductFilterSidebar({
  categories,
  activeCategory,
  onCategoryChange,
  manufacturers,
  selectedManufacturerIds,
  onToggleManufacturer,
  onlyMatching,
  onToggleOnlyMatching,
  matchHint,
  hasActiveFilters,
  onReset,
}: ProductFilterSidebarProps) {
  const { t } = useTranslation();
  return (
    <aside
      aria-label={t('creator.filter.title')}
      className="flex w-[220px] flex-none flex-col gap-5 border-r border-[var(--line)] p-4"
    >
      <div>
        <SectionTitle>{t('creator.filter.categories')}</SectionTitle>
        <div className="flex flex-col gap-0.5">
          {categories.map((category) => {
            const isActive = category.slug === activeCategory;
            return (
              <button
                key={category.id}
                type="button"
                aria-pressed={isActive}
                onClick={() => onCategoryChange(category.slug)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-control px-2 py-1.5 text-left text-[12.5px]',
                  'hover:bg-[var(--paper-2)]',
                  isActive
                    ? 'bg-[var(--paper-2)] font-semibold text-[var(--ink)]'
                    : 'text-[var(--ink-2)]'
                )}
              >
                <span className="min-w-0 truncate">{category.name}</span>
                <span className="dc-type-data text-[var(--mute)]">
                  {category.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <SectionTitle>{t('creator.filter.fit')}</SectionTitle>
        <CheckRow
          checked={onlyMatching}
          label={t('creator.filter.onlyMatching')}
          onClick={onToggleOnlyMatching}
          disabled={matchHint === null}
        />
        <p className="mt-1 px-2 text-[11px] leading-[1.45] text-[var(--mute)]">
          {matchHint ?? t('creator.filter.matchHintEmpty')}
        </p>
      </div>

      {manufacturers.length > 0 && (
        <div>
          <SectionTitle>{t('creator.filter.manufacturers')}</SectionTitle>
          <div className="flex flex-col gap-0.5">
            {manufacturers.map((manufacturer) => (
              <CheckRow
                key={manufacturer.id}
                checked={selectedManufacturerIds.includes(manufacturer.id)}
                label={manufacturer.name}
                count={manufacturer.count}
                onClick={() => onToggleManufacturer(manufacturer.id)}
              />
            ))}
          </div>
        </div>
      )}

      {hasActiveFilters && (
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1.5 rounded-control px-2 py-1.5 text-left text-[12px] text-[var(--ink-2)] hover:bg-[var(--paper-2)] hover:text-[var(--ink)]"
        >
          <X className="h-3.5 w-3.5" />
          {t('creator.filter.reset')}
        </button>
      )}
    </aside>
  );
}
